// Ver y bajar archivos de la cotización (recibos, fotos, PDF de la propuesta, portada). Con el servidor real se usa la
// URL directa (/api/archivos/:id, misma cookie); en modo simulado los bytes viven en el navegador y se arma una URL blob.
import { useEffect, useState } from 'react';
import { Download, FileText, Image as ImagenIcono } from 'lucide-react';
import { rutaCon } from '@cotizador/compartido';
import type { ArchivoSubido } from '@cotizador/compartido';
import { bajarArchivo, mensajeDe, transporteSimulado } from '../api/cliente.ts';
import { pdfAImagenes } from '../logica/pdf-a-imagenes.ts';
import { VisorZoom } from './visor-zoom.tsx';

export function usarUrlArchivo(id: string | null): { url: string | null; error: string | null } {
  const [estado, setEstado] = useState<{ url: string | null; error: string | null }>({ url: null, error: null });
  useEffect(() => {
    if (!id) {
      setEstado({ url: null, error: null });
      return;
    }
    if (!transporteSimulado()) {
      setEstado({ url: rutaCon('descargarArchivo', { archivoId: id }), error: null });
      return;
    }
    let vivo = true;
    let url: string | null = null;
    bajarArchivo(id)
      .then((b) => {
        url = URL.createObjectURL(b);
        if (vivo) setEstado({ url, error: null });
      })
      .catch((e: unknown) => vivo && setEstado({ url: null, error: mensajeDe(e) }));
    return () => {
      vivo = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);
  return estado;
}

/** Baja un archivo a la computadora o al teléfono con su nombre. */
export async function descargarComo(id: string, nombre: string): Promise<void> {
  const blob = await bajarArchivo(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function esImagen(a: Pick<ArchivoSubido, 'tipo_mime'>): boolean {
  return a.tipo_mime.startsWith('image/');
}

export function IconoArchivo({ archivo }: { archivo: Pick<ArchivoSubido, 'tipo_mime'> }) {
  return esImagen(archivo) ? <ImagenIcono aria-hidden /> : <FileText aria-hidden />;
}

/** Visor del cotejo: el recibo tal cual, al lado de lo que leyó la IA, con zoom y lupa (Diego, 13-oct: «no se alcanzan a
 *  ver los números»). El PDF se dibuja a imágenes para poder acercarlo igual que una foto (en el iPhone el <iframe> solo
 *  enseñaba la primera hoja y sin zoom); si el navegador no puede dibujarlo, queda el visor de PDF del navegador. */
export function VisorArchivo({ archivo }: { archivo: ArchivoSubido | null }) {
  const { url, error } = usarUrlArchivo(archivo?.id ?? null);
  const esPdf = !!archivo && !esImagen(archivo);
  const hojasPdf = usarHojasPdf(esPdf ? url : null);
  if (!archivo) return <div className="visor visor--vacio">Sin recibo que enseñar.</div>;
  if (error) return <div className="visor visor--vacio" role="alert">No se pudo abrir: {error}</div>;
  if (!url) return <div className="visor visor--vacio">Abriendo…</div>;
  if (archivo.tipo_mime === 'image/heic' || archivo.tipo_mime === 'image/heif')
    return (
      <div className="visor visor--vacio">
        El navegador no enseña fotos HEIC.{' '}
        <a href={url} download={archivo.nombre_original}>
          <Download aria-hidden /> Bajarla
        </a>
      </div>
    );
  if (esImagen(archivo)) return <VisorZoom hojas={[url]} alt={`Recibo: ${archivo.nombre_original}`} />;
  if (hojasPdf.hojas) return <VisorZoom hojas={hojasPdf.hojas} alt={`Recibo: ${archivo.nombre_original}`} />;
  if (!hojasPdf.fallo) return <div className="visor visor--vacio">Abriendo el PDF…</div>;
  return (
    <div className="visor">
      <iframe className="visor__pdf" src={`${url}#view=FitH&navpanes=0`} title={`Recibo: ${archivo.nombre_original}`} />
    </div>
  );
}

/** Las hojas del PDF dibujadas a imagen (URL blob, se liberan al cambiar de recibo). fallo = el navegador no pudo. */
function usarHojasPdf(url: string | null): { hojas: string[] | null; fallo: boolean } {
  const [estado, setEstado] = useState<{ url: string | null; hojas: string[] | null; fallo: boolean }>({ url: null, hojas: null, fallo: false });
  useEffect(() => {
    if (!url) return;
    const aviso = { cancelado: false };
    let hechas: string[] = [];
    setEstado({ url, hojas: null, fallo: false });
    pdfAImagenes(url, aviso)
      .then((h) => {
        hechas = h.map((x) => x.url);
        if (aviso.cancelado) hechas.forEach((u) => URL.revokeObjectURL(u));
        else setEstado({ url, hojas: hechas.length ? hechas : null, fallo: !hechas.length });
      })
      .catch(() => !aviso.cancelado && setEstado({ url, hojas: null, fallo: true }));
    return () => {
      aviso.cancelado = true;
      hechas.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [url]);
  // Mientras llega el estado del recibo nuevo no se enseñan las hojas del anterior.
  return estado.url === url ? { hojas: estado.hojas, fallo: estado.fallo } : { hojas: null, fallo: false };
}
